import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { FiChevronLeft, FiChevronRight } from "react-icons/fi"
import { useLang } from "hooks/useLang"

export interface CarouselProps<T> {
    items: T[]
    renderSlide: (item: T, index: number) => React.ReactNode
    autoPlay?: boolean
    autoPlayInterval?: number
    showDots?: boolean
    showArrows?: boolean
    initialIndex?: number
    activeIndex?: number
    className?: string
    trackClassName?: string
    onSlideChange?: (index: number) => void
    onSlideClick?: (item: T, index: number) => void
}

const Carousel = <T,>({
    items,
    renderSlide,
    autoPlay = true,
    autoPlayInterval = 5000,
    showDots = true,
    showArrows = true,
    initialIndex = 0,
    activeIndex,
    className = "",
    trackClassName = "",
    onSlideChange,
    onSlideClick,
}: CarouselProps<T>) => {
    const { isAr } = useLang()
    const [currentIndex, setCurrentIndex] = useState(initialIndex)
    const [dragOffset, setDragOffset] = useState(0)
    const [isHovering, setIsHovering] = useState(false)
    const [isInstant, setIsInstant] = useState(false)
    const containerRef = useRef<HTMLDivElement>(null)
    const isDraggingRef = useRef(false)
    const dragStartXRef = useRef(0)
    const dragStartYRef = useRef(0)
    const blockClickRef = useRef(false)
    const hasDragStartedRef = useRef(false)

    const itemCount = items.length
    const direction = isAr ? 1 : -1
    const baseOffset = useMemo(
        () => direction * currentIndex * 100,
        [direction, currentIndex]
    )

    const slideToIndex = useCallback(
        (nextIndex: number) => {
            if (itemCount === 0) return
            const normalized = (nextIndex + itemCount) % itemCount
            if (Math.abs(normalized - currentIndex) > 1) {
                setIsInstant(true)
            }
            setCurrentIndex(normalized)
            onSlideChange?.(normalized)
        },
        [currentIndex, itemCount, onSlideChange]
    )

    const handleNext = useCallback(() => {
        if (itemCount <= 1) return
        slideToIndex(isAr ? currentIndex - 1 : currentIndex + 1)
    }, [currentIndex, isAr, itemCount, slideToIndex])

    const handlePrev = useCallback(() => {
        if (itemCount <= 1) return
        slideToIndex(isAr ? currentIndex + 1 : currentIndex - 1)
    }, [currentIndex, isAr, itemCount, slideToIndex])

    useEffect(() => {
        if (!autoPlay || itemCount <= 1 || isHovering || isDraggingRef.current) return
        const timer = window.setInterval(handleNext, autoPlayInterval)
        return () => window.clearInterval(timer)
    }, [autoPlay, autoPlayInterval, handleNext, isHovering, itemCount])

    useEffect(() => {
        if (activeIndex === undefined) return
        if (activeIndex === currentIndex) return
        if (itemCount === 0) return
        const normalized = (activeIndex + itemCount) % itemCount
        if (Math.abs(normalized - currentIndex) > 1) {
            setIsInstant(true)
        }
        setCurrentIndex(normalized)
    }, [activeIndex, currentIndex, itemCount])

    useEffect(() => {
        if (!isInstant) return
        const timer = window.requestAnimationFrame(() => setIsInstant(false))
        return () => window.cancelAnimationFrame(timer)
    }, [isInstant])

    const getContainerWidth = () => containerRef.current?.clientWidth || 1

    const handleMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
        if (itemCount <= 1) return
        hasDragStartedRef.current = true
        isDraggingRef.current = false
        dragStartXRef.current = event.clientX
        dragStartYRef.current = event.clientY
        blockClickRef.current = false
        setDragOffset(0)
    }

    const handleMouseMove = (event: React.MouseEvent<HTMLDivElement>) => {
        if (!hasDragStartedRef.current) return
        const deltaX = event.clientX - dragStartXRef.current
        const deltaY = event.clientY - dragStartYRef.current
        const moved = Math.hypot(deltaX, deltaY)

        if (!isDraggingRef.current) {
            if (moved < 10) return
            if (Math.abs(deltaY) > Math.abs(deltaX)) {
                hasDragStartedRef.current = false
                return
            }
            isDraggingRef.current = true
            blockClickRef.current = true
        }

        setDragOffset(deltaX)
    }

    const handleMouseUp = () => {
        const wasDragging = isDraggingRef.current
        const offset = dragOffset
        isDraggingRef.current = false
        hasDragStartedRef.current = false
        setDragOffset(0)

        if (!wasDragging) {
            blockClickRef.current = false
            return
        }

        const containerWidth = getContainerWidth()
        const threshold = containerWidth * 0.2

        if (typeof window !== "undefined") {
            window.setTimeout(() => {
                blockClickRef.current = false
            }, 400)
        } else {
            blockClickRef.current = false
        }

        if (Math.abs(offset) < threshold) return
        if (offset > 0) {
            handlePrev()
        } else {
            handleNext()
        }
    }


    const trackStyle = useMemo(() => {
        const dragPercent = (dragOffset / getContainerWidth()) * 100
        return {
            transform: `translate3d(${baseOffset + dragPercent}%, 0, 0)`, // Use translate3d for GPU acceleration
            transition:
                isDraggingRef.current || isInstant
                    ? "none"
                    : "transform 400ms cubic-bezier(0.25, 0.1, 0.25, 1)", // Shorter, smoother easing
            willChange: isDraggingRef.current ? "transform" : "auto",
            backfaceVisibility: "hidden" as const,
            WebkitBackfaceVisibility: "hidden" as const,
        }
    }, [baseOffset, dragOffset, isInstant])

    const handleTouchStart = (event: React.TouchEvent<HTMLDivElement>) => {
        if (itemCount <= 1) return
        isDraggingRef.current = false
        hasDragStartedRef.current = false
        dragStartXRef.current = event.touches[0]?.clientX ?? 0
        dragStartYRef.current = event.touches[0]?.clientY ?? 0
        blockClickRef.current = false
        setDragOffset(0)
    }

    const handleTouchMove = (event: React.TouchEvent<HTMLDivElement>) => {
        const touchX = event.touches[0]?.clientX ?? 0
        const touchY = event.touches[0]?.clientY ?? 0
        const deltaX = touchX - dragStartXRef.current
        const deltaY = touchY - dragStartYRef.current
        const moved = Math.hypot(deltaX, deltaY)

        if (!hasDragStartedRef.current) {
            if (moved < 10) return
            hasDragStartedRef.current = true
            if (Math.abs(deltaY) > Math.abs(deltaX)) {
                return
            }
            isDraggingRef.current = true
            blockClickRef.current = true
        }

        if (!isDraggingRef.current) return

        setDragOffset(deltaX)
    }

    const handleTouchEnd = () => {
        const wasDragging = isDraggingRef.current
        const offset = dragOffset
        isDraggingRef.current = false
        hasDragStartedRef.current = false
        setDragOffset(0)

        if (!wasDragging) {
            blockClickRef.current = false
            return
        }

        const containerWidth = getContainerWidth()
        const threshold = containerWidth * 0.2

        if (typeof window !== "undefined") {
            window.setTimeout(() => {
                blockClickRef.current = false
            }, 400)
        } else {
            blockClickRef.current = false
        }

        if (Math.abs(offset) < threshold) return
        if (offset > 0) {
            handlePrev()
        } else {
            handleNext()
        }
    }

    if (itemCount === 0) {
        return null
    }

    return (
        <div
            className={`relative w-full overflow-hidden select-none cursor-grab active:cursor-grabbing ${className}`}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            onMouseEnter={() => setIsHovering(true)}
            onMouseLeave={(e) => {
                handleMouseUp()
                setIsHovering(false)
            }}
            onDragStart={(event) => event.preventDefault()}
            style={{ touchAction: "pan-y" }}
            ref={containerRef}
        >
            <div
                className={`flex h-full w-full select-none cursor-grab active:cursor-grabbing ${trackClassName}`}
                style={trackStyle}
            >
                {items.map((item, index) => (
                    <div
                        key={index}
                        className="w-full flex-shrink-0"
                        onClick={() => {
                  
                            if (!blockClickRef.current && onSlideClick) {
                                onSlideClick(item, index)
                            }
                        }}
                    >
                        {renderSlide(item, index)}
                    </div>
                ))}
            </div>

            {showArrows && itemCount > 1 && (
                <div className="pointer-events-none absolute inset-0 flex items-center justify-between px-4">
                    <button
                        type="button"
                        onClick={handlePrev}
                        className="pointer-events-auto rounded-full bg-white/80 p-2 text-gray-800 shadow transition hover:bg-white"
                        aria-label="Previous slide"
                    >
                        <FiChevronLeft className={isAr ? "rotate-180" : "rotate-0"} />
                    </button>
                    <button
                        type="button"
                        onClick={handleNext}
                        className="pointer-events-auto rounded-full bg-white/80 p-2 text-gray-800 shadow transition hover:bg-white"
                        aria-label="Next slide"
                    >
                        <FiChevronRight className={isAr ? "rotate-180" : "rotate-0"} />
                    </button>
                </div>
            )}

            {showDots && itemCount > 1 && (
                <div className="absolute bottom-4 left-0 flex w-full items-center justify-center gap-2">
                    {items.map((_, idx) => (
                        <button
                            key={idx}
                            type="button"
                            onClick={() => slideToIndex(idx)}
                            className={`h-2 rounded-full transition-all ${
                                currentIndex === idx
                                    ? "w-6 bg-white"
                                    : "w-2 bg-white/60"
                            }`}
                            aria-label={`Go to slide ${idx + 1}`}
                        />
                    ))}
                </div>
            )}
        </div>
    )
}

export default Carousel
